import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Columns, Column, Checkbox, Text, Badge, IconChevron } from "braid-design-system";
import { useContext, useState, useRef, useEffect } from "react";
import { MultiSelectDropdownContext, getCheckboxStatus, searchForMatchingOption } from "./MultiSelectDropdown.mjs";
import { alignSelfToStart } from "./MultiSelectDropdownOptionItem.css.mjs";
const MultiSelectDropdownOptionItem = ({
  multiSelectOption,
  leftIndent,
  isNvlEnabled,
  valueRenderer
}) => {
  const {
    search,
    onSelect,
    selected,
    filteredOptionKeys,
    onHoverOptionKey,
    refocus,
    mouseEnterHandler,
    mouseLeaveHandler,
    isMultipleLevel,
    expandCollapseHandler,
    expandedKeys
  } = useContext(MultiSelectDropdownContext);
  const [isExpanded, setIsExpanded] = useState(
    expandedKeys.includes(multiSelectOption.key)
  );
  const itemRef = useRef(null);
  const leftIndentIncrement = isNvlEnabled ? 12 : 20;
  const onExpandCollapse = () => {
    refocus == null ? void 0 : refocus();
    setIsExpanded((current) => !current);
    expandCollapseHandler(multiSelectOption.key);
  };
  const checkboxStatus = getCheckboxStatus(multiSelectOption, selected);
  const onMouseEnter = () => {
    mouseEnterHandler(multiSelectOption.key);
  };
  const handleOnSelect = () => {
    onSelect(multiSelectOption.key);
  };
  const getMultipleLevelIndicator = () => {
    if (isMultipleLevel) {
      return multiSelectOption.children ? /* @__PURE__ */ jsx(Box, { className: isNvlEnabled ? null : alignSelfToStart, children: /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
        Box,
        {
          cursor: "pointer",
          onClick: onExpandCollapse,
          paddingLeft: "xxsmall",
          "data-id": "multi-select-option-chevron",
          children: /* @__PURE__ */ jsx(
            IconChevron,
            {
              size: isNvlEnabled ? "xsmall" : "standard",
              direction: isExpanded ? "up" : "down"
            }
          )
        }
      ) }) }) : isNvlEnabled ? null : /* @__PURE__ */ jsx(Box, { paddingLeft: "gutter" });
    }
    return null;
  };
  useEffect(() => {
    var _a;
    if (multiSelectOption.key === onHoverOptionKey && itemRef.current && itemRef.current.scrollIntoView) {
      (_a = itemRef.current) == null ? void 0 : _a.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
        inline: "nearest"
      });
    }
  }, [onHoverOptionKey, multiSelectOption.key]);
  useEffect(() => {
    const hasLeafOptionFoundBySearch = Boolean(
      search && searchForMatchingOption(multiSelectOption, search)
    );
    if (hasLeafOptionFoundBySearch) {
      setIsExpanded(true);
    }
  }, [multiSelectOption, search]);
  if (!filteredOptionKeys.includes(multiSelectOption.key)) {
    return null;
  }
  const isOnHover = multiSelectOption.key === onHoverOptionKey;
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        borderRadius: "standard",
        paddingX: isNvlEnabled ? "xxsmall" : void 0,
        paddingY: isNvlEnabled ? "xsmall" : "small",
        paddingRight: isNvlEnabled ? void 0 : "xxsmall",
        style: { paddingLeft: leftIndent },
        background: isOnHover ? "formAccentSoft" : "surface",
        onMouseEnter,
        onMouseLeave: mouseLeaveHandler,
        "data-id": `multi-select-element-${multiSelectOption.key}`,
        ref: itemRef,
        children: /* @__PURE__ */ jsxs(Columns, { space: "none", alignY: "center", children: [
          /* @__PURE__ */ jsx(Box, { className: isNvlEnabled ? null : alignSelfToStart, children: /* @__PURE__ */ jsx(
            Column,
            {
              width: "content",
              data: { id: "multi-select-option-checkbox" },
              children: /* @__PURE__ */ jsx(
                Checkbox,
                {
                  data: { id: `${multiSelectOption.key}-checkbox` },
                  id: `${multiSelectOption.key}-checkbox`,
                  checked: checkboxStatus,
                  size: "small",
                  label: "",
                  onChange: handleOnSelect
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
            Box,
            {
              cursor: "pointer",
              display: "block",
              onClick: handleOnSelect,
              title: multiSelectOption.value,
              paddingY: isNvlEnabled ? void 0 : "xxsmall",
              paddingRight: "xxsmall",
              children: (valueRenderer == null ? void 0 : valueRenderer(multiSelectOption, checkboxStatus)) || /* @__PURE__ */ jsx(
                Text,
                {
                  maxLines: 2,
                  weight: checkboxStatus !== false ? "strong" : void 0,
                  tone: isNvlEnabled ? "secondary" : void 0,
                  children: multiSelectOption.value
                }
              )
            }
          ) }),
          multiSelectOption.count ? /* @__PURE__ */ jsx(Box, { className: isNvlEnabled ? null : alignSelfToStart, children: /* @__PURE__ */ jsx(Column, { width: "content", children: multiSelectOption.count ? /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: `${multiSelectOption.count}` }) : null }) }) : null,
          getMultipleLevelIndicator()
        ] })
      },
      multiSelectOption.key
    ),
    multiSelectOption.children && isExpanded && /* @__PURE__ */ jsx(Box, { children: multiSelectOption.children.map(
      (child, index) => filteredOptionKeys.includes(child.key) && /* @__PURE__ */ jsx(Box, { id: child.key, children: /* @__PURE__ */ jsx(
        MultiSelectDropdownOptionItem,
        {
          multiSelectOption: child,
          leftIndent: leftIndent + leftIndentIncrement,
          isNvlEnabled
        }
      ) }, `multi-select-option-${index}`)
    ) })
  ] });
};
export {
  MultiSelectDropdownOptionItem
};
